import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Volleyball, MailCheck } from 'lucide-react';

export default function RequestAccess() {
  const [form, setForm] = useState({ name: '', email: '', role: 'jeune', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/access-requests', form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-md w-full gold-glow"
        data-testid="request-access-page">
        <div className="flex items-center gap-2 font-fredoka font-semibold text-lg text-or-400 mb-8">
          <span className="w-9 h-9 rounded-full bg-or-400 flex items-center justify-center">
            <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
          </span>
          {t('requestAccess.title')}
        </div>
        {done ? (
          <div className="text-center" data-testid="request-access-success">
            <span className="w-16 h-16 rounded-full bg-or-400/15 flex items-center justify-center mx-auto mb-6">
              <MailCheck className="w-8 h-8 text-or-400" strokeWidth={2.5} />
            </span>
            <h1 className="text-2xl sm:text-3xl font-bold text-creme mb-4">{t('requestAccess.successTitle')}</h1>
            <p className="text-base text-brume leading-relaxed">
              {t('requestAccess.successText', { email: form.email })}
            </p>
            <Link to="/" data-testid="request-access-back-home">
              <Button className="mt-8 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold px-8">
                {t('common.backHome')}
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <p className="text-sm text-brume leading-relaxed">
              {t('requestAccess.intro')}
            </p>
            <div>
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="req-name">{t('requestAccess.name')}</label>
              <Input id="req-name" data-testid="request-name-input" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('requestAccess.namePlaceholder')}
                className="h-14 rounded-2xl text-base bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="req-email">{t('requestAccess.email')}</label>
              <Input id="req-email" data-testid="request-email-input" required type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('login.emailPlaceholder')}
                className="h-14 rounded-2xl text-base bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2">{t('requestAccess.roleLabel')}</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="request-role-select" className="h-14 rounded-2xl bg-nuit-900 border-nuit-600 text-creme font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-nuit-800 border-nuit-600 rounded-2xl">
                  <SelectItem value="jeune" data-testid="request-role-jeune" className="text-creme">{t('requestAccess.roleJeune')}</SelectItem>
                  <SelectItem value="parent" data-testid="request-role-parent" className="text-creme">{t('requestAccess.roleParent')}</SelectItem>
                  <SelectItem value="educateur" data-testid="request-role-educateur" className="text-creme">{t('requestAccess.roleEducateur')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="req-message">{t('requestAccess.motivation')}</label>
              <Textarea id="req-message" data-testid="request-message-input" rows={3} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t('requestAccess.motivationPlaceholder')}
                className="rounded-2xl bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40" />
            </div>
            <Button type="submit" data-testid="request-access-submit" disabled={loading}
              className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
              {loading ? t('common.sending') : t('requestAccess.submit')}
            </Button>
            <p className="text-center text-sm text-brume">
              <Link to="/" className="text-or-400 font-bold hover:underline" data-testid="request-back-link">{t('common.back')}</Link>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
